import { defaultHeight } from '@Common/Controls/SimpleGridLight/Editors/EditorProps';
import { BaseControl, DropDown, IListItem, InputDropDown } from '../../index';
import { IGridDropDownEditorProperties } from './GridEditors';
import { GridCell } from '../GridCell';
import { GridColumn } from '../GridColumn';
import { GridRow } from '../GridRow';
import { RowCollection } from '../RowCollection';
import { ICellEditor, ICellEditorWithText, IGridColumnEditor } from '../SimpleGridLight';
import { safeDestroys } from '@Common/Core/Destroyable';

export enum DropDownMode {
    dropDown,
    dropDownInput,
    dropDownInputById
}

export class CellDropDownEditor implements ICellEditor, ICellEditorWithText {
    public isEnabled: boolean;
    public view: JQuery;
    protected isInActive: boolean;
    private items: IListItem[];

    protected getTextDesc: (items) => any = items => {
        let text = this.prop.unknownValueText;
        if (items.length > 0) {
            text = items.map(item => item.name || item.value).join(', ');
        }
        return $('<span>', { text });
    };

    constructor(public prop: IGridDropDownEditorProperties, private readonly cell: GridCell) {
        this.view = $('<span>');
        this.view.appendTo(this.cell.tableCell());
        if (this.prop.getTitleDescription) {
            this.getTextDesc = this.prop.getTitleDescription;
        }
        this.refresh();
    }

    public cellValue(value?: any) {
        if (typeof value === 'undefined') {
            return this.cell.value();
        }
        this.cell.value(value);
        this.refresh();
    }

    public getInnerEditor(): BaseControl {
        throw new Error('not implemented CellDropDownEditor::getInnerEditor');
    }

    protected switchViewEditor(control1: JQuery, control2: JQuery) {
        control1.detach();
        control2.appendTo(this.cell.tableCell());
    }

    public beginEdit(preview = false) {
        const innerEditor = this.getInnerEditor() as DropDown;
        this.setSelectedItems(this.getSelectedItems());
        const control = innerEditor.getControl();
        this.switchViewEditor(this.view, control);
        if (!preview) {
            control.trigger('click');
        }
    }

    public endEdit() {
        const selected = this.getSelectedItems();
        this.view.html(this.getTextDesc(selected));
        this.switchViewEditor(this.getInnerEditor().getControl(), this.view);
    }

    public refresh() {
        const selected = this.getSelectedItems();
        if (selected.length === 0) {
            this.view.empty();
        } else {
            if (this.needUpdateInner()) {
                const dd = this.getInnerEditor() as DropDown;
                dd.selectedItems(selected);
            } else {
                this.view.html(this.getTextDesc(selected));
            }
        }
    }

    protected needUpdateInner(): boolean {
        return false;
    }

    public getSelectedItems(): IListItem[] {
        const value = this.cellValue();
        return this.getItems().filter(item =>
            _.isEqualWith(item.id, value, (v1, v2) => {
                return _.has(v1, 'id') && _.has(v2, 'id') ? v1.id === v2.id : v1 == v2;
            })
        );
    }

    public getItems(): IListItem[] {
        return this.items || this.prop.items || [];
    }

    public setItems(items: IListItem[]) {
        this.items = items;
        this.refresh();
    }

    private readonly(value: boolean) {
        return false;
    }

    public isInActiveMode(val?: boolean): boolean {
        if (typeof val === 'undefined') {
            return this.isInActive;
        } else {
            return (this.isInActive = val);
        }
    }

    public getText(): string {
        return this.view.text();
    }

    public setSelectedItems(items: IListItem[]) {}

    public destroy(): void {
        safeDestroys(this.view);
        this.items = null;
    }
}

const gridDropDownEditorDefaultProp: IGridDropDownEditorProperties = {
    unknownValueText: '',
    width: '100%',
    height: defaultHeight,
    dropDownMode: DropDownMode.dropDown
};

export class CellDropDownEditorModeDrop extends CellDropDownEditor {
    private inner: DropDown;
    private needSetItems = true;
    private readonly isSetState = false;

    public getInnerEditor(): BaseControl {
        if (!this.inner) {
            this.inner = new DropDown(this.prop);

            this.inner['onShowContent'](() => {
                this.isInActiveMode(true);
            });

            this.inner['onRemoveContent'](() => {
                this.isInActiveMode(false);
            });

            this.inner.onChange((value: any[]) => {
                if (!this.isSetState) {
                    if (this.prop.checkable) {
                        if (this.isInActive) {
                            this.cellValue(_.map(value, v => v.id));
                        }
                    } else {
                        this.cellValue(value.length > 0 ? value[0].id : null);
                    }
                    if (this.prop.onChange) {
                        this.prop.onChange();
                    }
                }
            });
        }
        if (!this.isInActive) {
            this.setState();
        }

        return this.inner;
    }

    protected needUpdateInner(): boolean {
        return this.inner && this.inner.getControl().elementInDOM() && !this.isInActive;
    }

    private setState() {
        if (this.needSetItems) {
            this.inner.setItems(this.getItems());
        }
        this.needSetItems = false;
        this.setSelectedItems(this.getSelectedItems());
    }

    public setItems(items: IListItem[]): void {
        this.needSetItems = true;
        super.setItems(items);
    }

    public getSelectedItems(): IListItem[] {
        const value = this.cellValue();
        if (this.prop.checkable) {
            return _.intersectionWith(this.getItems(), value, (item, value) => item.id === value);
        } else {
            return this.getItems().filter(item => item.id === value);
        }
    }

    public setSelectedItems(items: IListItem[]) {
        this.inner.selectedItems(items);
    }

    public destroy(): void {
        safeDestroys(this.inner);
        super.destroy();
    }
}

export class CellDropDownEditorModeInput extends CellDropDownEditor {
    private inner: InputDropDown;
    private readonly isIdMode: boolean;

    constructor(prop: IGridDropDownEditorProperties, cell: GridCell) {
        super(prop, cell);
        this.isIdMode = this.prop.dropDownMode === DropDownMode.dropDownInputById;
    }

    public getInnerEditor(): BaseControl {
        if (!this.inner) {
            this.inner = new InputDropDown(this.prop);

            this.inner.onShowContent(() => {
                this.isInActiveMode(true);
            });

            this.inner.onRemoveContent(() => {
                this.isInActiveMode(false);
            });

            if (this.prop.onChange) {
                this.inner.onChange((value: any[]) => {
                    this.prop.onChange();
                });
            }

            this.inner.getList().onItemClick((item: IListItem) => {});
        }

        this.inner.getList().items(this.getItems());
        const selectedValue = this.getSelectedItems();
        this.setSelectedItems(selectedValue);
        if (!selectedValue.length) {
            this.inner.getInput().val(this.cellValue());
        }
        return this.inner;
    }

    public setSelectedItems(items: IListItem[]) {
        this.inner.getList().selectedItems(items);
    }

    public getSelectedItems(): IListItem[] {
        const value = this.cellValue();
        return this.getItems().filter(item => (this.isIdMode ? item.id : item.name || item.value) == value);
    }

    private getInputValue(): string {
        if (this.isIdMode) {
            const selectedItems = this.inner.getList().selectedItems();
            if (selectedItems.length) {
                return selectedItems[0].id;
            }
        }
        return this.inner.getInput().val() as string;
    }

    protected needUpdateInner(): boolean {
        return this.inner && this.inner.getControl().elementInDOM() && !this.isInActive;
    }

    public endEdit() {
        const inputText = this.getInputValue();
        if (this.cellValue() !== inputText) {
            this.cellValue(inputText);
        }
        const selected = _.isEmpty(this.getSelectedItems())
            ? [
                {
                    id: inputText,
                    name: inputText
                }
            ]
            : this.getSelectedItems();
        this.view.html(this.getTextDesc(selected));
        this.switchViewEditor(this.getInnerEditor().getControl(), this.view);
    }

    public refresh() {
        const selected = this.getSelectedItems()[0];
        if (selected) {
            this.view.text(selected.name || selected.value);
        } else {
            this.view.text(this.cellValue());
        }
    }

    public destroy(): void {
        safeDestroys(this.inner);
        super.destroy();
    }
}

//TODO icon for dropdown
export class GridDropDownEditor<TId extends string | number = string> implements IGridColumnEditor {
    private properties: IGridDropDownEditorProperties;
    private rows: RowCollection;
    private column: GridColumn;

    constructor(prop?: IGridDropDownEditorProperties<TId> | any[]) {
        if (prop instanceof Array) {
            prop = { items: <any[]>prop };
        }
        this.properties = $.extend({}, gridDropDownEditorDefaultProp, prop);
    }

    public getEditor(row: GridRow, column: GridColumn): ICellEditor {
        const cell = row.getCell(column);
        let editor: CellDropDownEditor = null;
        if (this.properties.dropDownMode === DropDownMode.dropDown) {
            editor = new CellDropDownEditorModeDrop(this.properties, cell);
        } else {
            editor = new CellDropDownEditorModeInput(this.properties, cell);
        }
        if (this.properties.onCreateCellEditor) {
            this.properties.onCreateCellEditor(cell, editor);
        }
        return editor;
    }

    public buildEditorForAll(column: GridColumn) {}

    public refreshAll(rows: RowCollection) {
        this.rows = rows;
    }

    public init(column: GridColumn) {
        this.column = column;
    }

    public setItems(fieldItems: IListItem<TId>[]) {
        this.properties.items = fieldItems as any;
        const rows = this.rows.ToArray();
        for (const row of rows) {
            const cellDD = row.getCell(this.column).getEditor();
            if (cellDD instanceof CellDropDownEditor) {
                cellDD.setItems(fieldItems as any);
            }
        }
    }
}
